import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import moment from 'moment';
import Button from '../../atoms/button/Button';
import Loader from '../../organisms/loader/Loader';
import ReminderRichTextEditor from './ReminderRichTextEditor';
import { fetchCompanyDetails } from '../../../redux/companySlice';
import { setMaterial } from '../../../redux/materialSlice';
import { API_ENDPOINTS, ENDPOINTS } from '../utils/endpoints';
import { http_get, http_put } from '../utils/api_client';
import { BackIcon, TrashIcon, PencilIcon, Grid } from '../../assets/Icons';
import './RemindersPage.scss';

const PREVIEW_DEVICES = ['browser', 'mobile'];
const TRIGGER_OPTIONS = [
  { value: 'manual_nudge', label: 'Manual nudge' },
  { value: 'days_after_step_start', label: 'X days after step start if incomplete' },
  { value: 'days_before_due_date', label: 'X days before due date if incomplete' }
];

const getMessageKey = (message) => (message?.id ? `message-${message.id}` : message?.temp_id);
const buildPayload = (steps) => ({
  steps: steps.map((step) => ({
    uid: step.uid,
    messages: step.messages.map((message, index) => ({
      id: message.id,
      status: message.status,
      trigger_type: message.trigger_type,
      trigger_offset_days: Number(message.trigger_offset_days) || 0,
      send_at_time: message.send_at_time,
      subject: message.subject,
      preview_text: message.preview_text,
      body_html: message.body_html,
      position: index
    }))
  }))
});

const buildDefaultMessage = (step, count) => {
  const stepTitle = step?.title || step?.name || `Step ${step?.number || count + 1}`;
  const defaultTrigger = count === 0 ? 'manual_nudge' : (step?.due_date ? 'days_before_due_date' : 'days_after_step_start');

  return {
    id: null,
    temp_id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    position: count,
    trigger_type: defaultTrigger,
    trigger_offset_days: defaultTrigger === 'manual_nudge' ? 0 : 1,
    send_at_time: '',
    status: 'draft',
    subject: `Continue ${stepTitle}`,
    preview_text: `Keep moving through ${stepTitle}.`,
    body_html: `<p>Your step <strong>${stepTitle}</strong> is ready for you.</p><p>Use the button below to continue your onboarding.</p>`,
    published: false,
    published_label: 'Draft'
  };
};

const formatTriggerSummary = (message) => {
  switch (message?.trigger_type) {
    case 'manual_nudge':
      return 'Manual nudge';
    case 'days_after_step_start':
      return `${message?.trigger_offset_days || 0} day${Number(message?.trigger_offset_days || 0) === 1 ? '' : 's'} after start`;
    case 'days_before_due_date':
      return `${message?.trigger_offset_days || 0} day${Number(message?.trigger_offset_days || 0) === 1 ? '' : 's'} before due`;
    default:
      return 'Reminder';
  }
};

const hydrateSteps = (steps) => (
  Array.isArray(steps) ? steps.map((step) => ({
    ...step,
    messages: Array.isArray(step.messages) ? step.messages.map((message, index) => ({
      ...message,
      temp_id: message.id ? `message-${message.id}` : `draft-${step.uid}-${index}-${Math.random().toString(36).slice(2, 8)}`
    })) : []
  })) : []
);

const formatLastSavedLabel = (lastSavedAt) => {
  if (!lastSavedAt) return 'Not saved yet';

  return `Last saved ${moment(lastSavedAt).fromNow()}`;
};

const RemindersPage = () => {
  const dispatch = useDispatch();
  const { companySlug, lmSlug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pathInfo, setPathInfo] = useState(null);
  const [steps, setSteps] = useState([]);
  const [selectedStepUid, setSelectedStepUid] = useState(null);
  const [selectedMessageKey, setSelectedMessageKey] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('browser');
  const [isAddEmailPickerOpen, setIsAddEmailPickerOpen] = useState(false);
  const [editingMessageKey, setEditingMessageKey] = useState(null);

  const selectedStep = useMemo(
    () => steps.find((step) => step.uid === selectedStepUid) || steps[0] || null,
    [steps, selectedStepUid]
  );
  const selectedMessage = useMemo(() => {
    if (!selectedStep) return null;
    return selectedStep.messages.find((message) => getMessageKey(message) === selectedMessageKey)
      || selectedStep.messages[0]
      || null;
  }, [selectedStep, selectedMessageKey]);
  const isEditorUnlocked = !!selectedMessage && editingMessageKey === getMessageKey(selectedMessage);

  const loadData = () => {
    setIsLoading(true);
    setError(null);

    Promise.all([
      http_get(API_ENDPOINTS.GET_PATH(lmSlug)),
      http_get(API_ENDPOINTS.GET_PATH_REMINDERS(lmSlug))
    ])
      .then(([materialResponse, remindersResponse]) => {
        dispatch(setMaterial(materialResponse.data));
        const payload = remindersResponse.data || {};
        const hydratedSteps = hydrateSteps(payload.steps);
        setPathInfo(payload.path || null);
        setSteps(hydratedSteps);
        setSelectedStepUid((current) => current && hydratedSteps.some((step) => step.uid === current) ? current : hydratedSteps[0]?.uid || null);
        setSelectedMessageKey((current) => {
          const firstStep = hydratedSteps[0];
          const firstMessage = firstStep?.messages?.[0];
          if (current) {
            const exists = hydratedSteps.some((step) => step.messages.some((message) => getMessageKey(message) === current));
            if (exists) return current;
          }
          return firstMessage ? getMessageKey(firstMessage) : null;
        });
      })
      .catch((loadError) => {
        console.error(loadError);
        setError(loadError?.response?.data?.error || 'Unable to load reminder sequence for this path.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    dispatch(fetchCompanyDetails());
    loadData();
  }, [dispatch, lmSlug]);

  useEffect(() => {
    if (!selectedStep) {
      setSelectedMessageKey(null);
      return;
    }

    if (!selectedStep.messages.length) {
      setSelectedMessageKey(null);
      return;
    }

    const currentExists = selectedStep.messages.some((message) => getMessageKey(message) === selectedMessageKey);
    if (!currentExists) {
      const firstMessage = selectedStep.messages[0];
      setSelectedMessageKey(getMessageKey(firstMessage));
    }
  }, [selectedMessageKey, selectedStep]);

  const syncPersistedSteps = (responseData, selectionOverride = {}) => {
    const hydratedSteps = hydrateSteps(responseData?.steps);
    const nextSelectedStepUid = selectionOverride.selectedStepUid;
    const nextSelectedMessageKey = selectionOverride.selectedMessageKey;

    setPathInfo(responseData?.path || pathInfo);
    setSteps(hydratedSteps);

    const resolvedStepUid = nextSelectedStepUid && hydratedSteps.some((step) => step.uid === nextSelectedStepUid)
      ? nextSelectedStepUid
      : hydratedSteps[0]?.uid || null;

    setSelectedStepUid(resolvedStepUid);

    const resolvedStep = hydratedSteps.find((step) => step.uid === resolvedStepUid) || hydratedSteps[0];
    if (!resolvedStep?.messages?.length) {
      setSelectedMessageKey(null);
      setEditingMessageKey(null);
      return;
    }

    const matchByKey = nextSelectedMessageKey
      ? resolvedStep.messages.find((message) => getMessageKey(message) === nextSelectedMessageKey)
      : null;

    const resolvedMessageKey = matchByKey ? getMessageKey(matchByKey) : getMessageKey(resolvedStep.messages[0]);
    setSelectedMessageKey(resolvedMessageKey);
    setEditingMessageKey((current) => (current === nextSelectedMessageKey ? resolvedMessageKey : null));
  };

  const persistReminders = ({
    nextSteps,
    selection,
    restoreState = null,
    loadingMessage = true
  }) => {
    if (loadingMessage) {
      setIsSaving(true);
    }
    setError(null);

    return http_put(API_ENDPOINTS.PUT_PATH_REMINDERS(lmSlug), buildPayload(nextSteps))
      .then((response) => {
        syncPersistedSteps(response.data, selection);
        return response;
      })
      .catch((saveError) => {
        console.error(saveError);
        if (restoreState) {
          setSteps(restoreState.steps);
          setSelectedStepUid(restoreState.selectedStepUid);
          setSelectedMessageKey(restoreState.selectedMessageKey);
        }
        setError(saveError?.response?.data?.error || 'Unable to save reminder sequence right now.');
        throw saveError;
      })
      .finally(() => {
        if (loadingMessage) {
          setIsSaving(false);
        }
      });
  };

  const updateSelectedMessage = (updater) => {
    if (!selectedStep || !selectedMessage) return;

    const selectedKey = getMessageKey(selectedMessage);
    setSteps((currentSteps) => currentSteps.map((step) => {
      if (step.uid !== selectedStep.uid) return step;

      return {
        ...step,
        messages: step.messages.map((message) => {
          const messageKey = getMessageKey(message);
          return messageKey === selectedKey ? updater(message) : message;
        })
      };
    }));
  };

  const handleAddEmail = (stepUid) => {
    const targetStep = steps.find((step) => step.uid === stepUid);
    const nextMessage = buildDefaultMessage(targetStep, targetStep?.messages?.length || 0);

    setSelectedStepUid(stepUid);
    setSteps((currentSteps) => currentSteps.map((step) => {
      if (step.uid !== stepUid) return step;

      return {
        ...step,
        messages: [...step.messages, nextMessage].map((message, index) => ({ ...message, position: index }))
      };
    }));
    setSelectedMessageKey(nextMessage.temp_id);
    setEditingMessageKey(nextMessage.temp_id);
  };

  const openAddEmailPicker = () => {
    if (!steps.length) return;
    setIsAddEmailPickerOpen(true);
  };

  const closeAddEmailPicker = () => {
    setIsAddEmailPickerOpen(false);
  };

  const handleCreateEmailForStep = (stepUid) => {
    handleAddEmail(stepUid);
    closeAddEmailPicker();
  };

  const getRemovedState = (sourceSteps, stepUid, messageKey, currentStepUid, currentMessageKey) => {
    const targetStep = sourceSteps.find((step) => step.uid === stepUid);
    const nextMessages = targetStep?.messages
      ?.filter((message) => getMessageKey(message) !== messageKey)
      ?.map((message, index) => ({ ...message, position: index })) || [];
    const nextSelectedMessageKey = currentStepUid === stepUid && currentMessageKey === messageKey
      ? (nextMessages[0] ? getMessageKey(nextMessages[0]) : null)
      : currentMessageKey;

    return {
      nextSteps: sourceSteps.map((step) => {
        if (step.uid !== stepUid) return step;

        return {
          ...step,
          messages: nextMessages
        };
      }),
      nextSelectedStepUid: currentStepUid,
      nextSelectedMessageKey
    };
  };

  const handleDeleteSelectedMessage = () => {
    if (!selectedStep || !selectedMessage) return;
    const currentState = {
      steps,
      selectedStepUid,
      selectedMessageKey
    };
    const { nextSteps, nextSelectedStepUid, nextSelectedMessageKey } = getRemovedState(
      steps,
      selectedStep.uid,
      getMessageKey(selectedMessage),
      selectedStepUid,
      selectedMessageKey
    );

    setSteps(nextSteps);
    setSelectedStepUid(nextSelectedStepUid);
    setSelectedMessageKey(nextSelectedMessageKey);
    setEditingMessageKey(null);

    persistReminders({
      nextSteps,
      selection: {
        selectedStepUid: nextSelectedStepUid,
        selectedMessageKey: nextSelectedMessageKey
      },
      restoreState: currentState
    }).catch(() => { });
  };

  const handleSaveAll = () => {
    const selectedStepSnapshot = selectedStep?.uid || null;
    const selectedMessageSnapshot = selectedMessage ? getMessageKey(selectedMessage) : null;

    persistReminders({
      nextSteps: steps,
      selection: {
        selectedStepUid: selectedStepSnapshot,
        selectedMessageKey: selectedMessageSnapshot
      }
    }).catch(() => { });
  };

  const handleStartEditing = () => {
    if (!selectedMessage) return;

    setEditingMessageKey(getMessageKey(selectedMessage));
  };

  const selectedMessagePreview = DOMPurify.sanitize(selectedMessage?.body_html || '');

  return (
    <div className="un-path-reminders">
      <div className="un-path-reminders__header">
        <div className="un-path-reminders__header-left">
          <button type="button" className="un-path-reminders__back" onClick={() => { window.location.href = ENDPOINTS.EDIT_PATH(companySlug, lmSlug); }}>
            <BackIcon />
          </button>
          <div className="un-path-reminders__header-copy">
            <h1>Onboarding Sequence</h1>
            <p>
              Linked to Path:{' '}
              <a href={ENDPOINTS.EDIT_PATH(companySlug, lmSlug)}>
                {pathInfo?.name || lmSlug}
              </a>
              {pathInfo?.published_at ? ` · Last published ${moment(pathInfo.published_at).fromNow()}` : ''}
            </p>
          </div>
        </div>
        <div className="un-path-reminders__header-right">
          <span className="un-path-reminders__status-note">{formatLastSavedLabel(pathInfo?.last_saved_at)}</span>
          <Button className="un-path-reminders__save" onClick={handleSaveAll} isLoading={isSaving}>
            Save All
          </Button>
        </div>
      </div>

      {error && <div className="un-path-reminders__error">{error}</div>}
      <div className="un-path-reminders__banner">
        Manual nudge emails are sent only when an admin clicks <strong>Send nudge</strong>. Automated reminders only support time-based rules.
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="un-path-reminders__shell">
          <aside className="un-path-reminders__sidebar">
            <p className="un-path-reminders__sidebar-title">Sequence Emails</p>
            <div className="un-path-reminders__sidebar-scroll">
              {steps.map((step) => (
                <section key={step.uid} className="un-path-reminders__step-group">
                  <div className="un-path-reminders__step-label">
                    <span className="un-path-reminders__step-number">{step.number}</span>
                    <span>{step.title || step.name}</span>
                  </div>
                  <div className="un-path-reminders__email-list">
                    {step.messages.map((message) => {
                      const messageKey = getMessageKey(message);
                      return (
                        <button
                          key={messageKey}
                          type="button"
                          className={`un-path-reminders__email-card ${selectedStep?.uid === step.uid && getMessageKey(selectedMessage) === messageKey ? 'is-active' : ''}`}
                          onClick={() => {
                            setSelectedStepUid(step.uid);
                            setSelectedMessageKey(messageKey);
                            setEditingMessageKey(null);
                          }}
                        >
                          <div className="un-path-reminders__email-card-copy">
                            <strong>{message.subject || `Reminder ${message.position + 1}`}</strong>
                            <span className="un-path-reminders__email-grip" aria-hidden="true">
                              <Grid />
                            </span>
                            <div className="un-path-reminders__email-meta">
                              <span>{formatTriggerSummary(message)}</span>
                              <span className={`un-path-reminders__pill ${message.status === 'published' ? 'is-published' : 'is-draft'}`}>
                                {message.status === 'published' ? 'Published' : 'Draft'}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
            <div className="un-path-reminders__sidebar-footer">
              <button
                type="button"
                className="un-path-reminders__add-email"
                onClick={openAddEmailPicker}
                disabled={!steps.length}
              >
                <span className="un-path-reminders__add-email-icon" aria-hidden="true">+</span>
                <span className="un-path-reminders__add-email-label">Add Email</span>
              </button>
            </div>
          </aside>

          <section className="un-path-reminders__editor">
            <div className="un-path-reminders__editor-scroll">
              {!selectedStep || !selectedMessage ? (
                <div className="un-path-reminders__empty">
                  Select a reminder email on the left or add a new one to start authoring this sequence.
                </div>
              ) : (
                <div className="un-path-reminders__panel">
                  <div className="un-path-reminders__controls">
                    <div className="un-path-reminders__control">
                      <label>Status</label>
                      <select
                        value={selectedMessage.status}
                        disabled={!isEditorUnlocked}
                        onChange={(event) => updateSelectedMessage((message) => ({ ...message, status: event.target.value }))}
                      >
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>

                    <div className="un-path-reminders__control">
                      <label>When to send</label>
                      <div className="un-path-reminders__trigger-row">
                        <select
                          value={selectedMessage.trigger_type}
                          disabled={!isEditorUnlocked}
                          onChange={(event) => updateSelectedMessage((message) => ({
                            ...message,
                            trigger_type: event.target.value,
                            trigger_offset_days: event.target.value === 'manual_nudge' ? 0 : (Number(message.trigger_offset_days) || 1)
                          }))}
                        >
                          {TRIGGER_OPTIONS.map((option) => (
                            <option
                              key={option.value}
                              value={option.value}
                              disabled={option.value === 'days_before_due_date' && !selectedStep?.due_date}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          disabled={!isEditorUnlocked || selectedMessage.trigger_type === 'manual_nudge'}
                          value={selectedMessage.trigger_type === 'manual_nudge' ? 0 : selectedMessage.trigger_offset_days}
                          onChange={(event) => updateSelectedMessage((message) => ({ ...message, trigger_offset_days: event.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="un-path-reminders__control">
                      <label>Actions</label>
                      <div className="un-path-reminders__action-row">
                        <button
                          type="button"
                          className="un-path-reminders__icon-action"
                          onClick={handleStartEditing}
                          aria-label="Edit reminder template"
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          className="un-path-reminders__icon-action"
                          onClick={handleDeleteSelectedMessage}
                          aria-label="Delete reminder template"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>

                    <div className="un-path-reminders__control">
                      <label>Preview</label>
                      <div className="un-path-reminders__preview-toggle">
                        {PREVIEW_DEVICES.map((device) => (
                          <button
                            key={device}
                            type="button"
                            className={previewDevice === device ? 'is-active' : ''}
                            onClick={() => setPreviewDevice(device)}
                          >
                            {device === 'browser' ? 'Browser' : 'Mobile'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="un-path-reminders__fields">
                    <div className="un-path-reminders__field">
                      <label>Subject Line</label>
                      <input
                        type="text"
                        value={selectedMessage.subject || ''}
                        readOnly={!isEditorUnlocked}
                        onChange={(event) => updateSelectedMessage((message) => ({ ...message, subject: event.target.value }))}
                      />
                    </div>

                    <div className="un-path-reminders__field">
                      <label>Preview Text</label>
                      <input
                        type="text"
                        value={selectedMessage.preview_text || ''}
                        readOnly={!isEditorUnlocked}
                        onChange={(event) => updateSelectedMessage((message) => ({ ...message, preview_text: event.target.value }))}
                      />
                    </div>

                    <ReminderRichTextEditor
                      value={selectedMessage.body_html || ''}
                      readOnly={!isEditorUnlocked}
                      onChange={(html) => updateSelectedMessage((message) => ({ ...message, body_html: html }))}
                    />
                  </div>

                  <div className="un-path-reminders__preview">
                    <div className={`un-path-reminders__preview-shell ${previewDevice === 'mobile' ? 'is-mobile' : ''}`}>
                      <div className="un-path-reminders__preview-card">
                        <p className="un-path-reminders__preview-subject">{selectedMessage.subject || 'Reminder subject'}</p>
                        {selectedMessage.preview_text && <p className="un-path-reminders__preview-text">{selectedMessage.preview_text}</p>}
                        <div className="un-path-reminders__preview-body" dangerouslySetInnerHTML={{ __html: selectedMessagePreview }} />
                        <button type="button" className="un-path-reminders__preview-cta">Continue Path</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {isAddEmailPickerOpen && (
        <div className="un-path-reminders__picker-backdrop" onClick={closeAddEmailPicker}>
          <div className="un-path-reminders__picker-modal" onClick={(event) => event.stopPropagation()}>
            <div className="un-path-reminders__picker-header">
              <h2>Add Reminder Email</h2>
              <button type="button" className="un-path-reminders__picker-close" onClick={closeAddEmailPicker}>
                ×
              </button>
            </div>
            <p className="un-path-reminders__picker-copy">Select the step you want to add this reminder email to.</p>
            <div className="un-path-reminders__picker-list">
              {steps.map((step) => (
                <button
                  key={step.uid}
                  type="button"
                  className="un-path-reminders__picker-option"
                  onClick={() => handleCreateEmailForStep(step.uid)}
                >
                  <span className="un-path-reminders__picker-step-number">{step.number}</span>
                  <span className="un-path-reminders__picker-step-copy">
                    <strong>{step.title || step.name}</strong>
                    <span>{step.messages.length} email{step.messages.length === 1 ? '' : 's'}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindersPage;
